import { Trans } from "@lingui/react/macro";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import { Button } from "../button";

type WithRowId = { __rowId: string };

export function useRowAccordion<T extends WithRowId>(
  createRow: () => T,
  initialRows?: T[],
) {
  const [state, setState] = useState<{ rows: T[]; expandedRowId: string }>(
    () => {
      const rows = initialRows ?? [];
      return { rows, expandedRowId: rows[0]?.__rowId ?? "" };
    },
  );

  function initRows(rows: T[]) {
    setState({ rows, expandedRowId: rows[0]?.__rowId ?? "" });
  }

  function addRow() {
    const newRow = createRow();
    setState((prev) => ({
      rows: [...prev.rows, newRow],
      expandedRowId: newRow.__rowId,
    }));
  }

  function removeRow(rowId: string) {
    setState((prev) => {
      const index = prev.rows.findIndex((r) => r.__rowId === rowId);
      if (index === -1) return prev;
      const rows = prev.rows.filter((r) => r.__rowId !== rowId);
      const expandedRowId =
        prev.expandedRowId === rowId
          ? (rows[index]?.__rowId ?? rows[index - 1]?.__rowId ?? "")
          : prev.expandedRowId;
      return { rows, expandedRowId };
    });
  }

  function toggleRow(rowId: string) {
    setState((prev) => ({
      ...prev,
      expandedRowId: prev.expandedRowId === rowId ? "" : rowId,
    }));
  }

  return {
    rows: state.rows,
    expandedRowId: state.expandedRowId,
    initRows,
    addRow,
    removeRow,
    toggleRow,
  };
}

export function RowAccordion<T extends WithRowId>({
  rows,
  expandedRowId,
  toggleRow,
  removeRow,
  addRow,
  summaryLabel,
  renderRow,
}: {
  rows: T[];
  expandedRowId: string;
  toggleRow: (rowId: string) => void;
  removeRow: (rowId: string) => void;
  addRow: () => void;
  summaryLabel: (row: T, index: number) => React.ReactNode;
  renderRow: (row: T, index: number) => React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row, index) => {
        const isExpanded = expandedRowId === row.__rowId;
        return (
          <div key={row.__rowId} className="rounded-md border border-border">
            <div
              className={cn(
                "flex w-full items-center justify-between px-3 py-2 hover:bg-muted",
                isExpanded ? "rounded-t-md" : "rounded-md",
              )}
            >
              <button
                type="button"
                className="flex flex-1 items-center gap-2 bg-transparent p-0 text-left text-sm font-medium"
                onClick={() => toggleRow(row.__rowId)}
              >
                {isExpanded ? (
                  <ChevronDown
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                ) : (
                  <ChevronRight
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                )}
                {summaryLabel(row, index)}
              </button>
              {rows.length > 1 && (
                <button
                  type="button"
                  className="ml-2 bg-transparent p-0 text-xs text-destructive hover:text-destructive/80"
                  onClick={() => removeRow(row.__rowId)}
                >
                  <Trans>Remove</Trans>
                </button>
              )}
            </div>
            {isExpanded && (
              <div className="border-t border-border px-4 py-3 overflow-hidden">
                {renderRow(row, index)}
              </div>
            )}
          </div>
        );
      })}
      <Button type="button" variant="subtle" size="sm" onClick={addRow}>
        <Trans>+ Add Row</Trans>
      </Button>
    </div>
  );
}
